import {
  BadgeCheck, BarChart3, ClipboardCheck, ClipboardList, Coins, FileText, HandCoins,
  KeyRound, LayoutDashboard, MapPin, MessageSquare, Network, Receipt, ScanLine,
  ScrollText, ShieldCheck, Table2, Timer, UserCheck, Users, UsersRound, Wallet,
} from "lucide-react";

import { hasPermission as can } from "./rolePermissions";

const MANUAL_PLAYER_OTP_ENABLED =
  process.env.REACT_APP_ENABLE_MANUAL_PLAYER_OTP === "true";

export const MENU_GROUPS = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    items: [
      { to: "/dashboard", label: "Main Dashboard", visibleCheck: (role) => can(role, "dashboard") },
      // Analytics Dashboard is intentionally disabled.
      // {
      //   to: "/analytics",
      //   label: "Analytics Dashboard",
      //   visibleCheck: (role) => can(role, "reports"),
      // },
    ],
  },
  {
    id: "management",
    title: "Admins",
    icon: UsersRound,
    items: [
      {
        to: "/users",
        label: "Active Admins",
        visibleCheck: (role) => can(role, "usersView"),
      },
    ],
  },
  {
    id: "players",
    title: "Players",
    icon: Users,
    items: [
      {
        to: "/players",
        label: "All Players",
        visibleCheck: (role) => can(role, "playersView"),
      },
    ],
  },
  {
    id: "commission",
    title: "Rake",
    icon: Coins,
    hideFromNavigation: true,
    items: [
      {
        to: "/daily-commission",
        label: "Daily Rake",
        visibleCheck: (role) => role.isSuperAdmin,
      },
    ],
  },
  {
    id: "venue",
    title: "Venues",
    icon: MapPin,
    items: [
      {
        to: "/venues",
        label: "Venues",
        visibleCheck: (role) => can(role, "locationsView") || can(role, "clubsView"),
      },
    ],
  },
  {
    id: "administration",
    title: "Administration",
    icon: ShieldCheck,
    items: [
      { to: "/roles", label: "Roles", visibleCheck: (role) => can(role, "rolesView") },
      {
        to: "/audit-trail",
        label: "Audit Trail",
        visibleCheck: (role) => can(role, "auditTrailView"),
      },
      {
        to: "/invoice-template",
        label: "Invoice Template",
        visibleCheck: (role) => can(role, "invoiceTemplateManage"),
      },
    ],
  },
  {
    id: "qrtools",
    title: "Check-in & security",
    icon: ScanLine,
    items: [
      {
        to: "/qr-scanner",
        label: "QR Scanner",
        visibleCheck: (role) => can(role, "qrScanner"),
      },
      {
        to: "/manual-otp",
        label: "Player OTP",
        visibleCheck: (role) => MANUAL_PLAYER_OTP_ENABLED && can(role, "manualOTP"),
      },
      {
        to: "/checkin-list",
        label: "Check-In List",
        visibleCheck: (role) => can(role, "checkinListView"),
      },
      {
        to: "/manual-kyc",
        label: "Manual KYC",
        visibleCheck: (role) => can(role, "manualKYC"),
      },
    ],
  },
  {
    id: "transactions",
    title: "Money",
    icon: Wallet,
    items: [
      {
        to: "/admin/transactions",
        label: "Admin Transactions",
        visibleCheck: (role) => can(role, "transactionsView"),
      },
    ],
  },
  {
    id: "reports",
    title: "Reports",
    icon: BarChart3,
    items: [
      {
        to: "/reports",
        label: "Financial Reports",
        visibleCheck: (role) => can(role, "reports"),
      },
      {
        to: "/reports/affiliated",
        label: "Affiliated Reports",
        visibleCheck: (role) => can(role, "reports"),
      },
      {
        to: "/club-ledger",
        label: "Club Ledger",
        visibleCheck: (role) => can(role, "reports"),
      },
      {
        to: "/reports/table-player",
        label: "Player & Table Report",
        visibleCheck: (role) => can(role, "tablePlayerReportView"),
      },
    ],
  },
  {
    id: "messaging",
    title: "Messaging",
    icon: MessageSquare,
    items: [
      {
        to: "/messaging",
        label: "Send Message",
        visibleCheck: (role) => can(role, "messagingView"),
      },
    ],
  },
  {
    id: "dealer",
    title: "Dealers & tables",
    icon: Table2,
    items: [
      {
        to: "/dealer-management",
        label: "Table Management",
        visibleCheck: (role) => can(role, "dealer"),
      },
      {
        to: "/time-tracking",
        label: "Time Tracking",
        visibleCheck: (role) => can(role, "dealer"),
      },
      {
        to: "/dealer-audit",
        label: "Dealer Audit",
        visibleCheck: (role) => can(role, "dealerAuditView"),
      },
    ],
  },
  {
    id: "tips",
    title: "Tips",
    icon: HandCoins,
    hideFromNavigation: true,
    items: [
      {
        to: "/tips/dealer",
        label: "Dealer Tips",
        visibleCheck: (role) => role.isSuperAdmin,
      },
      {
        to: "/tips/service",
        label: "Service Tips",
        visibleCheck: (role) => role.isSuperAdmin,
      },
    ],
  },
];

// Whether a staff role may open the main dashboard (mirrors backend checkPermission('dashboard'))
export const canViewDashboard = (role) =>
  Boolean(role && (role.dashboard === true || role.isSuperAdmin === true));

// First page (in sidebar order) the role is allowed to open, or null if none
export const getFirstAllowedRoute = (role) => {
  if (!role || typeof role !== "object") return null;

  for (const group of MENU_GROUPS) {
    const items = group.subGroups
      ? group.subGroups.flatMap((subGroup) => subGroup.items)
      : group.items || [];
    const firstVisible = items.find((item) => item.visibleCheck(role));
    if (firstVisible) return firstVisible.to;
  }

  return null;
};

// Menu item guarding a path: exact match first, else the longest prefix
// (e.g. /users/:id -> /users, /roles/add -> /roles)
const findMenuItemForPath = (pathname) => {
  const items = MENU_GROUPS.flatMap((group) =>
    group.subGroups
      ? group.subGroups.flatMap((subGroup) => subGroup.items)
      : group.items || [],
  );
  const exact = items.find((item) => item.to === pathname);
  if (exact) return exact;
  return items
    .filter((item) => pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
};

// Whether the role may open a page; paths not in the menu are left open
export const canAccessRoute = (role, pathname) => {
  if (!role || typeof role !== "object") return true;
  if (role.isSuperAdmin === true) return true;
  const item = findMenuItemForPath(pathname);
  return item ? Boolean(item.visibleCheck(role)) : true;
};

// Icon per menu item (lucide-react)
export const ICON_MAPPING = {
  "Main Dashboard": LayoutDashboard,
  // "Analytics Dashboard": LineChart,
  "Active Admins": UsersRound,
  "All Players": Users,
  "Daily Rake": Coins,
  Venues: MapPin,
  Roles: ShieldCheck,
  "Audit Trail": ScrollText,
  "Invoice Template": Receipt,
  "QR Scanner": ScanLine,
  "Player OTP": KeyRound,
  "Check-In List": UserCheck,
  "Manual KYC": BadgeCheck,
  "Admin Transactions": Wallet,
  "Financial Reports": BarChart3,
  "Affiliated Reports": Network,
  "Club Ledger": FileText,
  "Player & Table Report": ClipboardList,
  "Send Message": MessageSquare,
  "Table Management": Table2,
  "Time Tracking": Timer,
  "Dealer Audit": ClipboardCheck,
  "Dealer Tips": HandCoins,
  "Service Tips": HandCoins,
};

// One plain sentence per page, shown under the page title in the top bar.
export const PAGE_DESCRIPTIONS = {
  "/dashboard": "Today at a glance: players, tables, rake and what needs attention.",
  // "/analytics": "Trends over time for rake, players and tables.",
  "/users": "Staff accounts that can sign in to the portal.",
  "/customers": "Customer accounts and their contact details.",
  "/players": "Every registered player, their KYC and their activity.",
  "/daily-commission": "Rake collected per table, ready to review and close each day.",
  "/venues": "Locations and the clubs inside them.",
  "/roles": "What each staff role is allowed to see and do.",
  "/audit-trail": "Who changed what, and when.",
  "/invoice-template": "The layout used on printed and PDF invoices.",
  "/qr-scanner": "Scan a player's QR code to check them in.",
  "/manual-otp": "Issue a one-time code for a player who can't receive SMS.",
  "/checkin-list": "Players who have checked in today.",
  "/manual-kyc": "Review and approve player identity documents.",
  "/reports": "Financial summaries by club and period.",
  "/reports/affiliated": "Revenue brought in through affiliates.",
  "/club-ledger": "Money in and out for each club.",
  "/reports/table-player": "Time and money per table and per player.",
  "/messaging": "Send SMS or app messages to players.",
  "/dealer-management": "Tables, dealers and live sessions on the floor.",
  "/time-tracking": "Dealer hours and breaks.",
  "/dealer-audit": "Dealer activity history for review.",
  "/tips/dealer": "Tips received by dealers.",
  "/tips/service": "Tips received by service staff.",
  "/affiliate": "Your affiliate earnings and referred players.",
  "/affiliate-management": "Affiliates, their players and payouts.",
};

// Menu item for a path (exact or nearest parent), with its group — for titles and breadcrumbs.
export const findMenuContext = (pathname) => {
  for (const group of MENU_GROUPS) {
    const items = group.subGroups ? group.subGroups.flatMap((g) => g.items) : group.items || [];
    const exact = items.find((item) => item.to === pathname);
    if (exact) return { group, item: exact };
  }
  let best = null;
  for (const group of MENU_GROUPS) {
    const items = group.subGroups ? group.subGroups.flatMap((g) => g.items) : group.items || [];
    for (const item of items) {
      if (pathname.startsWith(`${item.to}/`) && (!best || item.to.length > best.item.to.length)) best = { group, item };
    }
  }
  return best;
};
